import { useEffect, useMemo, useState } from "react";
import servicesFallback from "../../data/servicesData.json";
import trainingFallback from "../../data/trainingServices.json";
import { useCmsDoc } from "../../hooks/useCmsDoc";
import { Field, SaveBar, inputClass, textareaClass, saveCmsDoc } from "../cmsApi.jsx";
import { MoveButtons, moveListItem } from "../components/MoveListControls.jsx";

export default function CmsServicesEditor() {
  const fallback = useMemo(
    () => ({
      servicesPage: servicesFallback.servicesPage,
      servicesData: servicesFallback.servicesData,
      training: trainingFallback,
    }),
    []
  );
  const { data, loading, source } = useCmsDoc("services", "catalog", fallback);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    if (data) setForm(structuredClone(data));
  }, [data]);

  if (loading || !form) {
    return <p className="text-sm text-brand-dark/50">Cargando Servicios…</p>;
  }

  const categories = form.servicesData || [];
  const active = categories[activeIdx] || null;

  const updateCategory = (patch) => {
    setForm((prev) => {
      const servicesData = [...(prev.servicesData || [])];
      servicesData[activeIdx] = { ...servicesData[activeIdx], ...patch };
      return { ...prev, servicesData };
    });
  };

  const addCategory = () => {
    setForm((prev) => ({
      ...prev,
      servicesData: [
        ...(prev.servicesData || []),
        {
          id: `svc-${Date.now()}`,
          title: "Nueva categoría",
          sidebarTitle: "",
          description: "",
          tasksTitle: "",
          tasks: [],
          subServices: [],
        },
      ],
    }));
    setActiveIdx(categories.length);
  };

  const removeCategory = () => {
    if (!window.confirm("¿Eliminar esta categoría?")) return;
    setForm((prev) => ({
      ...prev,
      servicesData: (prev.servicesData || []).filter((_, i) => i !== activeIdx),
    }));
    setActiveIdx(0);
  };

  const moveCategory = (idx, dir) => {
    const nextIdx = idx + dir;
    if (nextIdx < 0 || nextIdx >= categories.length) return;
    setForm((prev) => ({
      ...prev,
      servicesData: moveListItem(prev.servicesData || [], idx, dir),
    }));
    if (activeIdx === idx) setActiveIdx(nextIdx);
    else if (activeIdx === nextIdx) setActiveIdx(idx);
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const { id, updatedAt, updatedBy, ...payload } = form;
      await saveCmsDoc("services", "catalog", payload);
      setMessage("Servicios guardados correctamente.");
    } catch (err) {
      setError(err?.message || "Error al guardar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <h2 className="font-display text-3xl font-black">Servicios</h2>
        <p className="text-sm text-brand-dark/55 mt-1">Fuente: {source}</p>
      </div>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Encabezado de página</h3>
        <Field label="Título">
          <input
            className={inputClass}
            value={form.servicesPage?.title || ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                servicesPage: { ...prev.servicesPage, title: e.target.value },
              }))
            }
          />
        </Field>
        <Field label="Subtítulo">
          <textarea
            className={textareaClass}
            value={form.servicesPage?.subtitle || ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                servicesPage: { ...prev.servicesPage, subtitle: e.target.value },
              }))
            }
          />
        </Field>
      </section>

      <div className="grid md:grid-cols-[240px_1fr] gap-4">
        <aside className="bg-white rounded-[28px] border border-brand-soft p-4 space-y-2">
          <div className="flex justify-between items-center mb-2">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-dark/40">Categorías</p>
            <button type="button" onClick={addCategory} className="text-xs font-bold text-brand-primary">
              +
            </button>
          </div>
          {categories.map((cat, idx) => (
            <div key={cat.id || idx} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex-1 text-left px-3 py-2 rounded-xl text-sm font-bold ${
                  idx === activeIdx ? "bg-brand-primary text-white" : "hover:bg-brand-soft/60"
                }`}
              >
                {cat.title || cat.id}
              </button>
              <MoveButtons
                idx={idx}
                total={categories.length}
                onMove={(dir) => moveCategory(idx, dir)}
              />
            </div>
          ))}
        </aside>

        {active && (
          <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
            <div className="flex justify-between">
              <h3 className="font-bold text-lg">Editar categoría</h3>
              <button type="button" onClick={removeCategory} className="text-xs text-red-600 font-bold">
                Eliminar
              </button>
            </div>
            <Field label="ID">
              <input className={inputClass} value={active.id || ""} onChange={(e) => updateCategory({ id: e.target.value })} />
            </Field>
            <Field label="Título">
              <input className={inputClass} value={active.title || ""} onChange={(e) => updateCategory({ title: e.target.value })} />
            </Field>
            <Field label="Título sidebar">
              <input
                className={inputClass}
                value={active.sidebarTitle || ""}
                onChange={(e) => updateCategory({ sidebarTitle: e.target.value })}
              />
            </Field>
            <Field label="Descripción">
              <textarea
                className={textareaClass}
                value={active.description || ""}
                onChange={(e) => updateCategory({ description: e.target.value })}
              />
            </Field>
            <Field label="Tareas (una por línea)">
              <textarea
                className={textareaClass}
                value={(active.tasks || []).join("\n")}
                onChange={(e) =>
                  updateCategory({
                    tasks: e.target.value
                      .split("\n")
                      .map((t) => t.trim())
                      .filter(Boolean),
                  })
                }
              />
            </Field>
            <div className="flex justify-between items-center pt-2">
              <h4 className="font-bold">Sub-servicios / productos</h4>
              <button
                type="button"
                className="text-xs font-bold uppercase tracking-widest text-brand-primary"
                onClick={() =>
                  updateCategory({
                    subServices: [
                      ...(active.subServices || []),
                      { title: "Nuevo", icon: "shield", desc: "", note: "" },
                    ],
                  })
                }
              >
                + Producto
              </button>
            </div>
            {(active.subServices || []).map((sub, sIdx) => (
              <div key={sIdx} className="border border-brand-soft rounded-2xl p-4 space-y-2">
                <div className="flex justify-between">
                  <p className="text-xs font-bold uppercase text-brand-dark/40">Producto {sIdx + 1}</p>
                  <button
                    type="button"
                    className="text-xs text-red-600 font-bold"
                    onClick={() =>
                      updateCategory({
                        subServices: (active.subServices || []).filter((_, i) => i !== sIdx),
                      })
                    }
                  >
                    Quitar
                  </button>
                </div>
                <input
                  className={inputClass}
                  value={sub.title || ""}
                  placeholder="Título"
                  onChange={(e) => {
                    const subServices = [...(active.subServices || [])];
                    subServices[sIdx] = { ...subServices[sIdx], title: e.target.value };
                    updateCategory({ subServices });
                  }}
                />
                <textarea
                  className={textareaClass}
                  value={sub.desc || ""}
                  placeholder="Descripción"
                  onChange={(e) => {
                    const subServices = [...(active.subServices || [])];
                    subServices[sIdx] = { ...subServices[sIdx], desc: e.target.value };
                    updateCategory({ subServices });
                  }}
                />
              </div>
            ))}
          </section>
        )}
      </div>

      <section className="bg-white rounded-[28px] border border-brand-soft p-6 space-y-4">
        <h3 className="font-bold text-lg">Capacitaciones (bloque training)</h3>
        <Field label="Título">
          <input
            className={inputClass}
            value={form.training?.title || ""}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                training: { ...prev.training, title: e.target.value },
              }))
            }
          />
        </Field>
        <p className="text-xs text-brand-dark/50">
          Estructura completa de bloques se conserva al guardar. Edición fina de ítems: use el JSON seed
          o amplíe este editor en una siguiente iteración.
        </p>
      </section>

      <SaveBar saving={saving} onSave={onSave} message={message} error={error} />
    </div>
  );
}
